import type { ReactElement } from 'react';
import { useMemo, useCallback } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { BrowserAiToolListItem } from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { SHADOW_AI_REFRESH_INTERVAL } from '@/hooks/api/useShadowAiTools';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  SHADOW_AI_TABLE_SCROLL_CLASS,
  BROWSER_TOOLS_HELPER_LINE,
} from '@/components/shared/shadow-ai';
import { createPersonalBrowserToolsColumns } from './columns';
import { fetchPersonalBrowserTools } from './api';
import { PERSONAL_BROWSER_AI_TOOLS_TABLE_CONFIG } from './constants';

const TABLE_ID = PERSONAL_BROWSER_AI_TOOLS_TABLE_CONFIG.tableId;

function buildQueryKeys(params: DataFetchParams) {
  return queryKeys.shadowAi.personalBrowserAiToolsDataTable({
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
  });
}

export function DataTablePersonalBrowserToolsContent(): ReactElement {
  const columns = useMemo(() => createPersonalBrowserToolsColumns(), []);

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchPersonalBrowserTools(params, signal),
    []
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) => buildQueryKeys(params),
    []
  );

  const searchConfig = useMemo(
    () => ({
      placeholder: 'Search browser AI tools...',
      className: 'w-[260px]',
    }),
    []
  );

  const featuresConfig = useMemo(() => ({ enableSorting: true }), []);

  const queryConfig = useMemo(
    () => ({
      staleTime: 30000,
      refetchOnWindowFocus: false,
      refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
    }),
    []
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Browser AI Tools</CardTitle>
        <CardDescription>{BROWSER_TOOLS_HELPER_LINE}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <DataTable<BrowserAiToolListItem>
          id={TABLE_ID}
          mode="server"
          className="flex flex-col"
          containerClassName={SHADOW_AI_TABLE_SCROLL_CLASS}
          columns={columns}
          errorBoundaryLabel="Something went wrong in the Browser AI Tools table"
          search={searchConfig}
          features={featuresConfig}
          queryFn={queryFn}
          getQueryKeysFromParams={queryKeyFn}
          noDataLabel="No browser AI tools found."
          queryConfig={queryConfig}
        />
      </CardContent>
    </Card>
  );
}

DataTablePersonalBrowserToolsContent.displayName =
  'DataTablePersonalBrowserToolsContent';
